'use client';

import { Icon } from '@magicschool/spellbook/components/Icon';
import { Text } from '@magicschool/spellbook/components/Text';
import { useRenderedChildren } from '@magicschool/spellbook/hooks/useRenderedChildren';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { useDirection } from 'radix-ui/direction';
import { Slot } from 'radix-ui/slot';
import { memo } from 'react';

/** Screen reader text appended to a link that opens in a new tab, unless `text.externalText` overrides it. */
export const DEFAULT_LINK_EXTERNAL_TEXT = '(opens in new tab)';

export interface LinkText {
  /** Text for screen readers indicating the link opens in a new tab. Defaults to '(opens in new tab)'. */
  externalText?: string;
}

export type LinkProps = React.ComponentProps<'a'> &
  VariantProps<typeof linkVariants> & {
    /** When true, renders children inside a `Slot` for composition with routing components like `next/link`. */
    asChild?: boolean;
    /** Prevents click events and applies disabled styling. Sets `aria-disabled="true"`. */
    disabled?: boolean;
    /** Configuration for component default text content, labels, placeholders, and screen reader announcements. */
    text?: LinkText;
    /** (optional) className to pass to the underlying `Text` component that is direct child of `Link`. */
    linkContentClassName?: string;
  };

export const linkVariants = cva(
  [
    'cursor-pointer',
    'outline-none',
    'transition-all',
    'focus-visible:border-ring',
    'focus-visible:ring-1',
    'focus-visible:ring-offset-0',
    'focus-visible:ring-focus-ring',
    'aria-invalid:ring-error-stroke',
    'overflow-hidden',
    'text-ellipsis',
    'group/link',
    'm-0',
    'p-0',
  ],
  {
    variants: {
      /** Visual style: `'default'` for standard link color, `'inverted'` for links on dark backgrounds, and `'muted'` for less prominent links". */
      variant: {
        default: '',
        inverted: '',
        muted: '',
      },
      /** If true, and the link is `isExternal` or `target=_blank` the external icon will show. Defaults to true. */
      showExternalIcon: {
        true: 'inline-flex items-center gap-1',
      },
      /** The size of the link. Only applicable to `standalone` variant. */
      size: {
        sm: '',
        md: '',
      },
      /** If true, the link will be disabled. */
      disabled: {
        true: 'cursor-not-allowed',
      },
      /** If true, the link will open in a new tab. */
      isExternal: {
        true: '',
        false: '',
      },
      asChild: {
        true: 'inline-flex items-center gap-1 text-sm font-medium', // hardcode `label-1` text props
      },
    },
  },
);

const linkTextVariants = cva(
  [
    'link',
    'transition-all',
    'no-underline',
    'group-hover/link:underline',
    'group-hover/link:underline-offset-3',
    'group-hover/link:decoration-[1px]',
    'cursor-pointer',
  ],
  {
    variants: {
      variant: {
        default: [
          'text-link-default-default',
          'group-hover/link:text-link-default-hover',
          'group-hover/link:decoration-link-default-hover',
          'group-active/link:text-link-default-active',
          'group-active/link:decoration-link-default-active',
        ],
        inverted: [
          'text-link-inverted-default',
          'group-hover/link:text-link-inverted-hover',
          'group-hover/link:decoration-link-inverted-hover',
          'group-active/link:text-link-inverted-active',
          'group-active/link:decoration-link-inverted-active',
        ],
        muted: [
          'text-link-muted-default',
          'group-hover/link:text-link-muted-hover',
          'group-hover/link:decoration-link-muted-hover',
          'group-active/link:text-link-muted-active',
          'group-active/link:decoration-link-muted-active',
        ],
      },
      disabled: {
        true: [
          'text-disabled-foreground',
          'transition-none',
          'focus-visible:ring-0',
          'cursor-not-allowed',
          'group-hover/link:decoration-transparent',
          'group-hover/link:text-disabled-foreground',
          'group-active/link:decoration-transparent',
          'group-active/link:text-disabled-foreground',
          'group-focus-visible/link:text-disabled-foreground',
          'group-focus-visible/link:ring-0',
        ],
      },
    },
  },
);

const linkIconVariants = cva('', {
  variants: {
    variant: {
      default: 'stroke-primary-icon',
      inverted: 'stroke-link-inverted-default',
      muted: 'stroke-default-icon',
    },
    disabled: {
      true: 'stroke-disabled-foreground',
    },
  },
});

type LinkContentProps = Pick<LinkProps, 'children' | 'disabled' | 'showExternalIcon' | 'variant' | 'size' | 'className'> & {
  isExternal: boolean;
  externalText?: string;
};

const LinkContent = memo<LinkContentProps>(
  ({ children, isExternal, externalText, disabled, size, showExternalIcon, variant, className }) => {
    const dir = useDirection();
    return (
      <>
        <Text
          variant={size == 'sm' ? 'caption-1' : 'label-1'}
          color="inherit"
          className={cn(linkTextVariants({ variant, disabled }), className)}
        >
          {children}
          {isExternal && externalText && <span className="sr-only"> {externalText}</span>}
        </Text>
        {isExternal && showExternalIcon && (
          <Icon
            name={dir === 'ltr' ? 'square-arrow-out-up-right' : 'square-arrow-out-up-left'}
            size="xs"
            color="inherit"
            className={cn(linkIconVariants({ disabled, variant }))}
          />
        )}
      </>
    );
  },
);

/**
 * Styled anchor element for navigation that supports external links, the `asChild` composition pattern, and visual variants.
 *
 * ### When to use
 * - For navigating to internal pages or external URLs
 * - With `asChild` to compose with `next/link` for client-side routing
 * - For inline links within text content or standalone navigational links
 *
 * ### When NOT to use
 * - For actions that don't navigate: use `Button` instead
 * - For icon-only clickable elements: use `IconButton` instead
 *
 * ### API
 * - `variant`: `'default'` for standard link color, `'inverted'` for use on dark backgrounds, and `'muted'` for less prominent links
 * - `size`: `'sm'` for compact text, `'md'` (default) for standard text
 * - `asChild`: Renders as its child element via Radix `Slot`, enabling composition with `next/link` or other routing components
 * - `disabled`: Prevents click events and applies disabled styling. Sets `aria-disabled="true"`
 * - `showExternalIcon`: Controls visibility of the external link icon when `target="_blank"`. Defaults to `true`
 * - `text.externalText`: Screen reader text appended for external links. Defaults to `'(opens in new tab)'`
 *
 * ### Visual states
 * - **Default**: Link-colored text with no underline
 * - **Hover**: Underline appears with hover-specific color
 * - **Active**: Active-specific color for text and underline
 * - **Disabled**: Muted text color, `cursor-not-allowed`, no underline on hover, `aria-disabled="true"`
 * - **Focus**: Focus ring via `focus-visible` styles
 *
 * ### Accessibility
 * - Automatically adds `rel="noopener noreferrer"` for `target="_blank"` links
 * - Appends screen-reader-only text (configurable via `text.externalText`) for external links
 * - Sets `aria-disabled="true"` when `disabled` and prevents click propagation
 * - When using `asChild`, adds `role="link"` to the composed element
 *
 * ### Usage with next/link
 * ```tsx
 * import NextLink from 'next/link';
 *
 * <Link asChild>
 *   <NextLink href="/dashboard">Dashboard</NextLink>
 * </Link>
 * ```
 */
export const Link: React.FC<LinkProps> = ({
  asChild,
  children,
  className,
  disabled,
  text = {},
  showExternalIcon = true,
  href,
  target,
  variant = 'default',
  size = 'md',
  onClick,
  linkContentClassName,
  ...props
}) => {
  const isExternal = target === '_blank';
  const { externalText = DEFAULT_LINK_EXTERNAL_TEXT } = text;

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (disabled) {
      // explicitly prevent default and stop propagation so that `asChild` components are also disabled
      event.preventDefault();
      event.stopPropagation();

      return;
    }

    onClick?.(event);
  };

  const { renderedChildren } = useRenderedChildren(children, {
    renderContent: ({ rest }) => (
      <LinkContent
        isExternal={isExternal}
        disabled={disabled}
        showExternalIcon={showExternalIcon}
        externalText={externalText}
        variant={variant}
        size={size}
        className={linkContentClassName}
      >
        {rest}
      </LinkContent>
    ),
  });

  if (asChild) {
    return (
      <Slot
        data-spellbook-name="link"
        role="link"
        className={cn(linkVariants({ variant, size, disabled, isExternal, asChild, showExternalIcon }), className)}
        aria-disabled={disabled ? true : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        {...props}
        onClick={handleOnClick}
      >
        {renderedChildren}
      </Slot>
    );
  }

  return (
    <a
      data-spellbook-name="link"
      className={cn(linkVariants({ variant, size, disabled, isExternal, asChild, showExternalIcon }), className)}
      target={target}
      href={href}
      aria-disabled={disabled ? true : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      {...props}
      onClick={handleOnClick}
    >
      <LinkContent
        isExternal={isExternal}
        disabled={disabled}
        showExternalIcon={showExternalIcon}
        externalText={externalText}
        variant={variant}
        size={size}
        className={linkContentClassName}
      >
        {children}
      </LinkContent>
    </a>
  );
};
